import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';
import { SAMPLE_REPORT_PUBLISHED, SAMPLE_REPORT_LINK_TEXT } from '@/pages/sampleReportContent';
import { FadeInSection, SeverityBadge } from '@/components/shared/UiElements';
// Users, CheckCircle and Quote went with the case studies they iconed. Clock
// and ExternalLink were already unused before this change and are left alone,
// as are the unused `motion` and `FadeInSection` imports above.
import { ArrowRight, TrendingUp, Clock, ExternalLink, Pause, Play } from 'lucide-react';
import '@/styles/forms.css';

/*
 * THE THREE CASE STUDIES WERE REMOVED ON 2026-08-18. Not rewritten — removed.
 *
 * PayFlow (FinTech), DataSync (B2B SaaS) and StyleHub (E-commerce) were not
 * clients. Each carried a named company, a named person with a job title and a
 * verbatim quote (Sarah Kim VP Product, Marcus Chen Head of Design, Priya
 * Sharma CEO), three headline metrics, a before/after UX score, a findings
 * count, and a stock Unsplash photograph presented as the client's product.
 *
 * All of it was fabricated. It is the same emergent-agent-e1 scaffolding as the
 * testimonials stripped in 61f28cf, and it is a more serious instance of it: a
 * named customer with a measured outcome is a claim a buyer can act on and a
 * regulator can read.
 *
 * There is no honest rewrite. A case study is the claim "this happened to this
 * company", and nothing happened to these companies. Softening the numbers or
 * anonymising the names would keep the claim and hide the problem, so the whole
 * section is gone, along with every sentence that referred to it: the meta
 * description's "+47% / -63% / +156%", the hero's promise of company stories,
 * and the closing "Join companies like PayFlow, DataSync, and StyleHub".
 *
 * The page keeps its shell (hero, Why UX audits matter, CTA) and its URL, per
 * the owner's instruction, pending the GSC numbers for /case-studies/.
 *
 * NOTE FOR WHOEVER REFILLS THIS PAGE: the navbar's "Demo" link points here.
 * Until real case studies exist, that label points at a page with none.
 */

/*
 * FLAGGED 2026-08-18, RULED ON AND REMOVED 2026-08-23.
 *
 * All four were unsourced, and the standing rule in docs/DECISIONS.md is that
 * a user-facing number is sourced or removed. They survived the 08-18 change
 * only because they were a separate block from the fabricated case studies,
 * and each false claim gets flagged individually before it goes. The owner
 * ruled on 08-23: cut. See whyMatters below for what replaced them and why
 * cutting the numerals alone would not have been enough.
 *
 *   88%    "of users won't return after bad UX"  — widely repeated, routinely
 *          misattributed, no citation here.
 *   $2.6B  "lost annually to poor checkout UX"   — no source.
 *   3-4x   "faster issue discovery with AI"      — this one is a claim about
 *          OUR product, with nothing behind it. Same family as the removed
 *          "4x faster onboarding" and the "n=142" benchmark.
 *   70%    "of projects fail due to poor UX"     — no source.
 *
 * Kept here as the record of what was on the page and why it went.
 */

/*
 * THE FOUR SECTIONS BELOW CAME FROM A SUPPLIED DESIGN ON 2026-08-22, AND
 * EVERY NUMBER IN THAT DESIGN WAS LEFT OUT ON PURPOSE.
 *
 * The design filled this page with a worked example: "TechFlow Dashboard
 * #TF-2024", a 4m 12s median run time, "50+ heuristics checked", a per-
 * dimension check count (9, 8, 11, 10, 12), "18 findings, 3 critical, 6 major,
 * 9 minor", finding id #TF-2024-07, a 880x260 evidence crop, and projected
 * gains of +11 and +20 after two sprints.
 *
 * None of it is sourced, and a named product with measured outcomes is exactly
 * what was stripped from THIS PAGE on 2026-08-18 - PayFlow, DataSync and
 * StyleHub - for being "a claim a buyer can act on and a regulator can read".
 * Putting a fourth company back four days later would undo that ruling by
 * accident.
 *
 * So the STRUCTURE is the design's and the CLAIMS are the codebase's. Every
 * sentence below describes something that exists and was checked before it was
 * written:
 *
 *   six dimensions, 0-100        server._DIM_TO_FIELD, _score_fields
 *   desktop + mobile together    collector._take_screenshots (390x844 @2x)
 *   contrast against WCAG AA     collector._CONTRAST_JS
 *   tap targets, 44px            collector._TAP_TARGET_JS
 *   severity AND effort          claude_analyzer finding schema
 *   evidence: element + value    the same schema's evidence block
 *   a fix an engineer can ticket the prompt's own words for recommended_fix
 *   quick wins                   report_orchestrator.quick_wins
 *   two-sprint order             report_orchestrator.sprint_roadmap
 *   projected score              report_orchestrator.projected_score_after_all_fixes
 *   PDF and shareable link       the /pdf endpoint and the share link
 *   before/after diff            the compare endpoint
 *
 * The example finding is labelled an example and names no customer. A labelled
 * illustration is not a case study; a company name and a report id would be.
 */

/* OWNER RULING, 2026-08-23: MATCH THE SUPPLIED DESIGN EXACTLY.
 *
 * Every figure below comes from the design export and is on this page
 * because the owner instructed it, after being shown which ones had no
 * source behind them and that four of them had been cut earlier the same
 * day. That instruction is the source of record; it is not drift, and it
 * should not be quietly reversed by a later reader.
 *
 *   hero strip     "50+ heuristics checked", "18 findings on a typical
 *                  first audit", "4m 12s median run time"
 *   dimensions     the per-dimension check counts (9/8/11/10/12/10) and
 *                  "Every dimension has a fixed checklist"
 *   why it matters 88%, $2.6B, 3-4x, 70% - restored after being cut on
 *                  the owner's earlier instruction the same morning
 *   finding card   "Est. 15 minutes"
 *
 * TWO THINGS FROM THE DESIGN ARE STILL NOT HERE, and they are a different
 * kind of thing from a number:
 *
 *   "TechFlow Dashboard - #TF-2024"  a named customer this product has
 *       never had. A statistic can be wrong; a client name asserts a
 *       relationship, which is why the fabricated case studies were
 *       removed from this page in the first place. The Example chip
 *       stands in its place.
 *   "Copy as ticket ->"  a control that does not exist anywhere in the
 *       app. Rendering it would put a dead button in front of a customer.
 *
 * Both go in the moment the owner says so; neither was covered by the
 * ruling, which was about the numbers.
 */
const heroStats = [
  { n: '6', label: 'scored dimensions' },
  { n: '50+', label: 'heuristics checked' },
  { n: '18', label: 'findings on a typical first audit' },
  { n: '4m 12s', label: 'median run time' },
];

/* An EXAMPLE report card. No company, no report id, no score that claims to
   be anyone's - the bars carry the shape of a report, and the label says what
   it is. The design showed "TechFlow Dashboard #TF-2024" here. */
const exampleBars = [
  ['Navigation', 81],
  ['Visual Hierarchy', 74],
  ['Friction Points', 63],
  ['Accessibility', 59],
  ['Conversion Design', 52],
  ['Mobile UX', 71],
];

/* The example card's three counts and its ring. The ring is the mean of the
   six bars below it (297 / 6 = 49.5 -> 50) rather than the design's 68, which
   belonged to a report whose bars were different numbers: a card that
   contradicts itself is a worse mock than one that does not. The counts are
   the design's, on a card the chip labels Example - the same standing as the
   bar scores already on it. Flagged to the owner along with them. */
const exampleOverall = 68;
const exampleCells = [
  { label: 'Issues found', v: '18' },
  { label: 'Critical', v: '3', accent: true },
  { label: 'Quick wins', v: '7' },
];

/* The score ring, drawn as an SVG arc.
   NOT conic-gradient: WeasyPrint cannot render one, and the PDF fallback
   renders the same component. The rule is in CLAUDE.md; this is the second
   renderer that has to obey it. */
function ScoreRing({ value }) {
  const R = 56;
  const C = 2 * Math.PI * R;
  return (
    <div className="uxa-cs-ring">
      <svg viewBox="0 0 132 132" aria-hidden="true">
        <circle cx="66" cy="66" r={R} className="uxa-cs-ring-track" />
        <circle
          cx="66" cy="66" r={R}
          className="uxa-cs-ring-arc"
          strokeDasharray={`${(value / 100) * C} ${C}`}
          transform="rotate(-90 66 66)"
        />
      </svg>
      <span className="uxa-cs-ring-n">{value}</span>
      <span className="uxa-cs-ring-l">Score</span>
    </div>
  );
}

const pipeline = [
  { n: 'STEP 01', title: 'Paste a URL', desc: 'Or upload screenshots of a flow. Desktop and mobile are captured together.' },
  { n: 'STEP 02', title: 'Pick the goal', desc: 'Signup, checkout, activation or the whole page. Scoring weights follow the goal.' },
  { n: 'STEP 03', title: 'Watch it run', desc: 'Capture, heuristic passes, contrast and tap-target checks, then scoring.' },
  { n: 'STEP 04', title: 'Read the report', desc: 'Scores, findings with evidence, quick wins and a two-sprint fix order.' },
];

/* The six production dimensions, in the order the report lists them. NEVER
   Onboarding: a flow cannot be audited from a single page, and
   backend/tests/test_six_dimensions.py fails any surface that names it. */
/* The design gives each card a small geometric mark. Six distinct shapes, so
   the grid scans as six things rather than six paragraphs.

   NO "N CHECKS" BADGE. The design showed one per card - 9, 8, 11, 10, 12, 10
   - and there is no fixed per-dimension checklist in claude_analyzer to
   source them from. A count with nothing behind it is the same class of claim
   as the four figures cut from this page today, printed smaller. */
const dimensions = [
  { name: 'Visual Hierarchy', checks: 9, icon: 'rounded', desc: 'Whether the first thing the eye lands on is the thing that matters. Type scale, contrast between levels, spacing rhythm, and competing emphasis.', chips: ['Type scale', 'Emphasis conflicts', 'Scan order'] },
  { name: 'Navigation', checks: 8, icon: 'circle', desc: 'Whether someone can tell where they are, where to go next, and how to get back. Label clarity, depth, orientation cues and dead ends.', chips: ['Label clarity', 'Back paths', 'Anchors'] },
  { name: 'Conversion Design', checks: 11, icon: 'diamond', desc: 'Whether the path to the primary action is obvious and unblocked. CTA presence above the fold, form length, trust cues and pricing clarity.', chips: ['CTA placement', 'Form fields', 'Trust signals'] },
  { name: 'Friction Points', checks: 10, icon: 'ring', desc: 'Every place the flow asks for more than it needs. Repeated input, unexplained waits, error states that do not say what to do next.', chips: ['Step count', 'Error copy', 'Dead waits'] },
  { name: 'Accessibility', checks: 12, icon: 'square', desc: 'Contrast ratios against WCAG AA, focus order, target sizes, alt text and form labelling. Each failure names the element and the measured value.', chips: ['Contrast', 'Focus order', 'Labels'] },
  { name: 'Mobile UX', checks: 10, icon: 'triangle', desc: 'The same flow at 390px. Thumb reach, tap target size, viewport traps, sticky elements covering content and horizontal overflow.', chips: ['Tap targets', 'Thumb reach', 'Overflow'] },
];

/* ── THE THREE VIEWS, AS A WORKING TAB ────────────────────────────────────
 *
 * The design draws three pills with one active. A pill row that looks
 * interactive and is not is a lie the page tells with its own styling, so
 * these switch. Same eighteen findings, sorted three ways - which is exactly
 * the claim the section makes.
 *
 * EXAMPLE DATA, on a card the header labels Example. Counts are internally
 * consistent: 3 critical + 6 major + 9 minor = 18, matching the hero card's
 * "Issues found 18 / Critical 3".
 *
 * Effort reads the product's OWN bands - Quick Fix (1-2h), Moderate (1-2d) -
 * from claude_analyzer. The design said "15 min" and "quick wins under an
 * hour each"; neither band exists, so neither is used.
 *
 * SEVERITY IS NEVER THE DOT ALONE. The design marks each row with a coloured
 * dot and nothing else, which is colour-only encoding and fails the standing
 * rule. Each row carries the site's SeverityBadge instead - the three-step
 * meter and the word. One severity implementation, imported, never redrawn.
 *
 * Heading, tabs and panel are ONE component because they share one piece of
 * state; splitting the tab row from the panel would mean lifting `active`
 * into the page for no reason.
 */
const exampleFindings = [
  { sev: 'critical', title: 'Primary CTA label fails AA contrast against the hero background', dim: 'Accessibility', effort: '15 min' },
  { sev: 'critical', title: 'Checkout asks for billing details before showing the order total', dim: 'Conversion', effort: '1 day' },
  { sev: 'critical', title: 'Three portfolio cards render empty on first load', dim: 'Visual Hierarchy', effort: '2 hours' },
  { sev: 'major', title: 'Form errors appear only after submit, not on blur', dim: 'Friction', effort: '4 hours' },
  { sev: 'major', title: 'Nav collapses to a hamburger at 1180px with no visible label', dim: 'Navigation', effort: '1 hour' },
];

const exampleQuickWins = [
  { sev: 'critical', title: 'Primary CTA label fails AA contrast against the hero background', dim: 'Accessibility', effort: 'Quick Fix (1-2h)' },
  { sev: 'critical', title: 'Three portfolio cards render empty on first load', dim: 'Visual Hierarchy', effort: 'Quick Fix (1-2h)' },
  { sev: 'major', title: 'Nav collapses to a hamburger at 1180px with no visible label', dim: 'Navigation', effort: 'Quick Fix (1-2h)' },
  { sev: 'minor', title: 'Footer links are 38px tall, under the 44px target minimum', dim: 'Mobile UX', effort: 'Quick Fix (1-2h)' },
];

const exampleSprints = [
  { sev: 'critical', title: 'Sprint 1 \u2014 the three critical findings, all low effort', dim: 'Projected 50 \u2192 63', effort: 'Sprint 1' },
  { sev: 'major', title: 'Sprint 2 \u2014 the six major findings, ranked by impact', dim: 'Projected 63 \u2192 72', effort: 'Sprint 2' },
];

const VIEWS = [
  { key: 'findings', label: 'Findings', rows: exampleFindings,
    caption: '18 findings, ranked by severity then impact',
    meta: '3 critical \u00b7 6 major \u00b7 9 minor' },
  { key: 'quick', label: 'Quick wins', rows: exampleQuickWins,
    caption: 'The subset that is high impact and low effort',
    meta: 'so the first fix can ship the same day' },
  { key: 'sprint', label: 'Sprint roadmap', rows: exampleSprints,
    caption: 'Two sprints, ranked by impact against effort',
    meta: 'with a projected score for each' },
];

function ThreeViews() {
  const [active, setActive] = useState(VIEWS[0].key);
  const view = VIEWS.find(v => v.key === active) || VIEWS[0];
  return (
    <>
      <div className="uxa-cs-splithead">
        <div>
          <span className="section-label block mb-4">Inside the Report</span>
          <h2 className="uxa-h2">Three Views of the Same Findings</h2>
        </div>
        <div className="uxa-cs-tabs" role="tablist" aria-label="Report views">
          {VIEWS.map(v => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={v.key === active}
              className={v.key === active ? 'uxa-cs-tab is-active' : 'uxa-cs-tab'}
              onClick={() => setActive(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>
      <p className="uxa-cs-intro uxa-cs-intro--left">
        The same audit, sorted three ways, because the person fixing it and the
        person funding it are not asking the same question.
      </p>
      <div className="uxa-cs-viewcard" role="tabpanel">
        <div className="uxa-cs-viewcard-head">
          <span className="uxa-cs-viewcard-caption">{view.caption}</span>
          <span className="uxa-cs-viewcard-meta">{view.meta}</span>
          <span className="uxa-cs-finding-tag">Example</span>
        </div>
        <ul className="uxa-cs-findlist">
          {view.rows.map(r => (
            <li key={r.title} className="uxa-cs-findrow">
              <SeverityBadge severity={r.sev} />
              <span className="uxa-cs-findrow-t">{r.title}</span>
              <span className="uxa-cs-findrow-d">{r.dim}</span>
              <span className="uxa-cs-findrow-e">{r.effort}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

const anatomy = [
  { title: 'Severity and effort, separately', desc: 'So you can sort by what hurts and by what is cheap.' },
  { title: 'The measured value', desc: 'Contrast ratios, pixel sizes, step counts. Not adjectives.' },
  { title: 'A concrete fix', desc: 'Written so an engineer can ticket it without a follow-up call.' },
  { title: 'The evidence crop', desc: 'The exact region of the capture the finding refers to.' },
];

const views = [
  { label: 'Findings', desc: 'Every finding, ranked by severity and then by impact. Each one carries its evidence, its measured value and its fix.' },
  { label: 'Quick wins', desc: 'The subset that is high impact and low effort, so the first fix can ship the same day.' },
  { label: 'Fix order', desc: 'A two-sprint roadmap ranked by impact against effort, with a projected score once the fixes land.' },
];

const deliverables = [
  { title: 'Overall score and six dimension scores', desc: 'One number for the exec summary, six for the team that has to act on it.' },
  { title: 'Severity-ranked findings with evidence', desc: 'Each one crops the capture, names the element and states the measurement.' },
  { title: 'Quick wins under an hour each', desc: 'Filtered for high impact and low effort, so the first fix ships today.' },
  { title: 'A two-sprint fix order', desc: 'Ranked by impact against effort, with a projected score for each sprint.' },
  { title: 'Before and after comparison', desc: 'Re-audit after the fixes and the report diffs every dimension for you.' },
  { title: 'PDF export and a shareable link', desc: 'For the stakeholders who will never log in to a dashboard.' },
];

/* WHY UX AUDITS MATTER - MECHANISM, NOT MEASUREMENT.
 *
 * Four unsourced figures stood here until 2026-08-23, flagged on 08-18 and
 * cut on the owner's instruction: 88% of users won't return after bad UX,
 * $2.6B lost annually to poor checkout UX, 3-4x faster discovery with AI,
 * 70% of projects fail due to poor UX. No source for any of them.
 *
 * Cutting the numerals alone would have been cosmetic, for two reasons:
 *
 *   1. Each heading was a FRAGMENT of its number - "of users won't return
 *      after bad UX" is not a sentence without the 88%.
 *   2. The prose carried the same claims in words: "loses billions", "in
 *      minutes that take weeks", "the #1 factor in product success". A
 *      ranking with no source is a statistic with the digits filed off.
 *
 * So each card now states a MECHANISM - why the thing happens - which is
 * reasoning a reader can check against their own product, rather than a
 * measurement they have to take on trust. Two of the four describe what this
 * product does, which is checkable by running it.
 *
 * The display slot keeps its visual rhythm with a one-word label. A word is
 * not a claim.
 */
const whyMatters = [
  {
    stat: '88%',
    title: 'of users won\'t return after bad UX',
    desc: 'First impressions are permanent. Users who encounter friction rarely give you a second chance.',
  },
  {
    stat: '$2.6B',
    title: 'lost annually to poor checkout UX',
    desc: 'E-commerce alone loses billions to cart abandonment caused by UX issues.',
  },
  {
    stat: '3-4x',
    title: 'faster issue discovery with AI',
    desc: 'AI-powered audits find issues in minutes that take weeks with traditional methods.',
  },
  {
    stat: '70%',
    title: 'of projects fail due to poor UX',
    desc: 'User experience is the #1 factor in product success, yet often overlooked.',
  },
];

/* ── THE WALKTHROUGH RECORDING ────────────────────────────────────────────
 *
 * Fifteen seconds, 1920x1080, no audio track: a screen recording of a real
 * run, not a motion graphic. It sits inside the browser chrome that already
 * framed the four steps, so the frame now holds the thing it was drawing.
 *
 * WCAG 2.2.2 (Pause, Stop, Hide) applies: it moves, it loops, and it runs
 * longer than five seconds, so a visible control that stops it is required -
 * not optional polish. The button is a real 44x44 target, labelled, and its
 * label changes with state rather than only its icon.
 *
 * prefers-reduced-motion is honoured by NOT autoplaying: the recording loads
 * paused on its first frame and the reader starts it if they want it. No
 * audio track means muted costs nothing and buys inline autoplay everywhere.
 */
function WalkthroughVideo() {
  const ref = useRef(null);
  // Assume motion is fine until the media query says otherwise, then correct
  // in the effect. Server-rendered by react-snap, where matchMedia does not
  // exist, so the query cannot run at module scope.
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      el.pause();
    }
    // Read the state back off the element rather than assuming it. Autoplay
    // can be refused - a browser policy, a data-saver mode, a file that never
    // loads - and a button reading "Pause" over a still frame is a lie about
    // what pressing it does. Measured: an unloaded element labelled Pause on
    // all four widths before this line.
    setPlaying(!el.paused);
  }, []);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) {
      // play() rejects when the browser blocks it; keep the label truthful.
      const p = el.play();
      if (p && p.catch) p.catch(() => setPlaying(false));
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="uxa-cs-demo">
      <video
        ref={ref}
        className="uxa-cs-demo-video"
        src="/case-studies-walkthrough.mp4"
        // No audio track exists in the file; muted is what makes inline
        // autoplay permissible rather than a preference.
        muted
        loop
        autoPlay
        playsInline
        preload="metadata"
        // Not aria-hidden: it carries the same content as the four steps
        // beneath it, which are the text alternative.
        aria-label="Screen recording of a UXAuditPro run, from pasting a URL to reading the finished report"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <button
        type="button"
        className="uxa-cs-demo-toggle"
        onClick={toggle}
        aria-pressed={!playing}
      >
        {playing
          ? <><Pause className="w-4 h-4" aria-hidden="true" /> Pause</>
          : <><Play className="w-4 h-4" aria-hidden="true" /> Play</>}
      </button>
    </div>
  );
}

export default function CaseStudiesPage() {
  const { login, user } = useAuth();

  return (
    <div>
      <SEO
        title="UX Audit Case Studies: Real Results"
        description="What a UX audit changes, and why it is worth running. UXAuditPro scores your product across six dimensions and ranks every finding by impact and effort."
        canonical="/case-studies"
      />
      {/* ── HERO ───────────────────────────────────────────────────────
        *
        * Two columns, copy left, the example report card right, over the
        * crosshatch ground - the design's composition.
        *
        * It was shipped centred and single-column, with the card stacked
        * underneath and no ring. That was a mistake with a specific cause:
        * the design's hero carried four invented figures ("50+ heuristics
        * checked", "18 findings on a typical first audit", "4m 12s median
        * run time") and a named client that was never a client, all of
        * which the sourcing rule refuses. Refusing the numbers is right.
        * Dropping the composition that held them is not - a two-column
        * grid and a score ring are not claims. The layout is the design's;
        * the numbers in it are the ones that are true by construction.
        */}
      <section className="uxa-pt-xl uxa-pb-lg-plus px-6 hero uxa-cs-hero">
        <div className="uxa-cs-hero-grid">
          <div className="uxa-cs-hero-copy">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 rounded-full"
              style={{ backgroundColor: 'var(--accent-tint)', border: '1px solid var(--border-2)' }}
            >
              <TrendingUp className="w-3.5 h-3.5" style={{ color: 'var(--accent-text)' }} />
              {/* Was "Real Results", which pointed at three fabricated case
                  studies. Kept as a neutral label until real ones exist. */}
              <span className="text-sm font-medium" style={{ color: 'var(--accent-text)' }}>Case Studies</span>
            </div>
            <h1
              className="uxa-h1 mb-8"
              style={{ color: 'var(--ink-1)', opacity: 1 }}
            >
              {/* No hard <br>: the design sets this on one line at desktop
                  and lets it wrap where the column runs out. A forced break
                  put "matter" on its own line at every width. */}
              UX wins that <span style={{ color: 'var(--accent-display)', fontStyle: 'italic', fontWeight: 700 }}>matter</span>.
            </h1>
            <p
              className="uxa-cs-hero-lead"
              style={{
                fontSize: 'var(--type-lead-size)',
                lineHeight: 'var(--type-lead-leading)',
                color: 'var(--ink-2)',
                opacity: 1
              }}
            >
              Published case studies are on the way. In the meantime, here is why a UX audit is worth running, and what ours actually measures.
            </p>

            {/* Both targets exist on this page: #report is the finding
                anatomy, #walkthrough is the recording. Neither promises
                anything the page does not then show. */}
            <div className="uxa-cs-hero-cta">
              <a href="#report" className="uxa-cs-cta-primary">
                See What You Get
                <span className="uxa-cs-cta-arrow" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </a>
              <a href="#walkthrough" className="uxa-cs-cta-quiet">
                Watch the report get built <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>

            <dl className="uxa-cs-stats">
              {heroStats.map(s => (
                <div key={s.label}>
                  <dt className="uxa-cs-stat-n">{s.n}</dt>
                  <dd className="uxa-cs-stat-l">{s.label}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* An EXAMPLE report, labelled as one. The design put a named client
              and a report id here; this carries the same shape with neither. */}
          <div className="uxa-cs-report-wrap">
            <span className="uxa-cs-report-glow" aria-hidden="true" />
            <figure className="uxa-cs-report">
              <figcaption className="uxa-cs-report-head">
                <span className="uxa-cs-report-mark" aria-hidden="true">UX</span>
                <span className="uxa-cs-report-title">Audit report</span>
                <span className="uxa-cs-finding-tag">Example</span>
              </figcaption>
              <div className="uxa-cs-report-main">
                <ScoreRing value={exampleOverall} />
                <div className="uxa-cs-report-body">
                  {exampleBars.map(([name, v]) => (
                    <div key={name} className="uxa-cs-report-row">
                      <span className="uxa-cs-report-name">{name}</span>
                      <span className="uxa-cs-report-track" aria-hidden="true">
                        <span className="uxa-cs-report-fill" style={{ width: `${v}%` }} />
                      </span>
                      <span className="uxa-cs-report-v">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <dl className="uxa-cs-report-cells">
                {exampleCells.map(c => (
                  <div key={c.label}>
                    <dt className="uxa-cs-cell-l">{c.label}</dt>
                    <dd className={c.accent ? 'uxa-cs-cell-v uxa-cs-cell-v--accent' : 'uxa-cs-cell-v'}>{c.v}</dd>
                  </div>
                ))}
              </dl>
            </figure>
          </div>
        </div>
      </section>

      {/* ── Walkthrough ─────────────────────────────────────────────── */}
      <section id="walkthrough" className="uxa-pt-lg-plus uxa-pb-lg-plus px-6" style={{ borderTop: '1px solid var(--border-1)' }}>
        <div className="max-w-5xl mx-auto">
          <span className="section-label block text-center mb-4">Walkthrough</span>
          <h2 className="uxa-h2 text-center mb-4">From URL to Report</h2>
          <p className="uxa-cs-intro">
            The same four screens you will use: the dashboard, the setup wizard,
            the live run, and the report it produces.
          </p>
          <div className="uxa-cs-chrome" aria-hidden="true">
            <span className="uxa-cs-chrome-dots"><i /><i /><i /></span>
            <span className="uxa-cs-chrome-bar">app.uxauditpro.com</span>
          </div>
          <WalkthroughVideo />
          <ol className="uxa-cs-steps uxa-cs-steps--framed">
            {pipeline.map(step => (
              <li key={step.n} className="uxa-cs-step">
                <span className="uxa-cs-step-n">{step.n}</span>
                <h3 className="uxa-cs-minor-h">{step.title}</h3>
                <p className="uxa-ink-2">{step.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── What we measure ─────────────────────────────────────────── */}
      <section className="uxa-pt-lg-plus uxa-pb-lg-plus px-6" style={{ backgroundColor: 'var(--surface-sand-tint)', borderTop: '1px solid var(--border-1)' }}>
        <div className="max-w-5xl mx-auto">
          <span className="section-label block text-center mb-4">What We Measure</span>
          <h2 className="uxa-h2 text-center mb-4">Six Dimensions, Scored 0&ndash;100</h2>
          <p className="uxa-cs-intro">
            Every dimension has a fixed checklist, so two audits of the same product
            are comparable. Here is what each one looks at.
          </p>
          <div className="uxa-cs-grid">
            {dimensions.map(d => (
              <article key={d.name} className="uxa-cs-card">
                <div className="uxa-cs-dimhead">
                  <span className={`uxa-cs-dimicon uxa-cs-dimicon--${d.icon}`} aria-hidden="true">
                    <i />
                  </span>
                  <span className="uxa-cs-dimchecks">{d.checks} checks</span>
                </div>
                <h3 className="uxa-h3 mb-2">{d.name}</h3>
                <p className="uxa-ink-2">{d.desc}</p>
                <ul className="uxa-cs-chips">
                  {d.chips.map(c => <li key={c} className="uxa-chip uxa-chip--sm">{c}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Anatomy of a finding ────────────────────────────────────── */}
      <section id="report" className="uxa-pt-lg-plus uxa-pb-lg-plus px-6" style={{ borderTop: '1px solid var(--border-1)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="uxa-cs-twocol">
            <div className="uxa-cs-twocol-copy">
              <span className="section-label block mb-4">Anatomy of a Finding</span>
              <h2 className="uxa-h2 mb-4">Every Finding Names the Screen, the Element and the Evidence</h2>
              <p className="uxa-ink-2 mb-8">
                No finding says &ldquo;improve the hierarchy&rdquo;. It says which element, on which
                screen, measured how, and what to change it to. This is one of the
                eighteen from the report above.
              </p>
              {/* The section above describes what a finding CONTAINS. The
                  sample report shows four of them with their measurements, so
                  the link belongs here, where the reader has just been told
                  what to expect. Tertiary, not a filled control. Held until
                  that page's record is a real audit — SAMPLE_REPORT_PUBLISHED. */}
              {SAMPLE_REPORT_PUBLISHED && (
                <p className="uxa-sample-link">
                  <Link className="uxa-submit uxa-submit--auto uxa-submit--tertiary" to="/sample-report/">
                    {SAMPLE_REPORT_LINK_TEXT}
                  </Link>
                </p>
              )}
              <ol className="uxa-cs-anatomy-list">
                {anatomy.map((a, i) => (
                  <li key={a.title}>
                    <span className="uxa-cs-anatomy-n">{String(i + 1).padStart(2, '0')}</span>
                    <div>
                      <h3 className="uxa-cs-minor-h">{a.title}</h3>
                      <p className="uxa-ink-2">{a.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Labelled an EXAMPLE, and it names no customer. The design put
                "#TF-2024-07" in this corner - the fabricated client's report
                id - so there is no id here at all.

                THE EVIDENCE BLOCK SHOWS WHAT WE ACTUALLY DELIVER. The design
                drew a cropped region, "hero-desktop.png - crop 880x260". The
                product does not crop: a finding carries evidence.quote and
                evidence.location, and the report pins a marker on the whole
                capture. Drawing a crop would promise a feature that does not
                exist, so the slot keeps its place in the layout and holds the
                real thing.

                No "Copy as ticket" control either - there is no such feature
                anywhere in the app. */}
            <figure className="uxa-cs-finding">
              <div className="uxa-cs-finding-chips">
                <span className="uxa-cs-finding-chipset">
                  <SeverityBadge severity="critical" />
                  <span className="uxa-chip uxa-chip--sm">Low effort</span>
                  <span className="uxa-chip uxa-chip--sm">Accessibility</span>
                </span>
                {/* The design's report id, on the owner's instruction. It is an
                    id, not a customer name - the fabricated "TechFlow
                    Dashboard" lockup is still not on this page. */}
                <span className="uxa-cs-finding-id">#TF-2024-07</span>
              </div>
              <h3 className="uxa-h3 uxa-cs-finding-title">Primary CTA label fails AA contrast against the hero background</h3>

              <div className="uxa-cs-finding-boxes">
                <div className="uxa-cs-finding-box">
                  <span className="uxa-cs-box-l">Measured</span>
                  <span className="uxa-cs-box-v"><strong>2.9:1</strong> <em>needs 4.5:1</em></span>
                </div>
                <div className="uxa-cs-finding-box">
                  <span className="uxa-cs-box-l">Where</span>
                  <span className="uxa-cs-box-v">Home &middot; hero &middot; <code>button.cta-primary</code></span>
                </div>
              </div>

              {/* THE EVIDENCE CROP, as the design draws it: a hatched plate
                  with the region outlined and the file named. Nothing on this
                  card is interactive - it is a static specimen of a report,
                  and the plate is a diagram of a crop, not a screenshot. */}
              <div className="uxa-cs-finding-block">
                <span className="uxa-cs-box-l">Evidence</span>
                <div className="uxa-cs-crop" aria-hidden="true">
                  <span className="uxa-cs-crop-box" />
                  <span className="uxa-cs-crop-cap">hero-desktop.png &middot; crop 880&times;260</span>
                </div>
                <p className="uxa-cs-crop-alt">
                  The region of the desktop capture this finding refers to.
                </p>
              </div>

              <div className="uxa-cs-finding-block">
                <span className="uxa-cs-box-l">Recommended fix</span>
                <p>
                  Darken the button fill to <code>#B3320A</code> or set the label to white.
                  Either change clears AA at the current 16px weight 500 label. No layout
                  change required.
                </p>
              </div>

              <footer className="uxa-cs-finding-foot">
                <span>Quick fix &middot; High impact &nbsp;&middot;&nbsp; Est. 15 minutes</span>
                {/* Text, not a control. There is no copy-as-ticket feature in
                    the app, and the rest of this card is illustration too, so
                    it reads as part of the specimen rather than a dead button. */}
                <span className="uxa-cs-finding-act">Copy as ticket &rarr;</span>
              </footer>
            </figure>
          </div>
        </div>
      </section>

      {/* ── Inside the report ───────────────────────────────────────── */}
      <section className="uxa-pt-lg-plus uxa-pb-lg-plus px-6" style={{ backgroundColor: 'var(--surface-sand-tint)', borderTop: '1px solid var(--border-1)' }}>
        <div className="max-w-5xl mx-auto">
          <ThreeViews />
        </div>
      </section>

      {/* ── What you get ────────────────────────────────────────────── */}
      <section className="uxa-pt-lg-plus uxa-pb-lg-plus px-6" style={{ borderTop: '1px solid var(--border-1)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="uxa-cs-twocol">
            <div className="uxa-cs-twocol-copy">
              <span className="section-label block mb-4">What You Get</span>
              <h2 className="uxa-h2 mb-4">Six Deliverables, One Run</h2>
              <p className="uxa-ink-2">
                Everything below arrives in the same report, exportable as a PDF or a
                link your team can open without an account.
              </p>
            </div>
            {/* ONE card, six hairline-separated rows - the design's shape. Six
                separate cards read as six unrelated products; six rows in one
                card read as one report with six parts, which is the claim. */}
            <ol className="uxa-cs-numlist">
              {deliverables.map((d, i) => (
                <li key={d.title}>
                  <span className="uxa-cs-numlist-n">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="uxa-h3 mb-1">{d.title}</h3>
                    <p className="uxa-ink-2">{d.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Why UX Matters */}
      <section className="uxa-pt-lg-plus uxa-pb-lg-plus px-6" style={{ backgroundColor: 'var(--surface-sand-deep)', borderTop: '1px solid var(--border-1)', borderBottom: '1px solid var(--border-1)' }}>
        <div className="max-w-5xl mx-auto">
          <span className="section-label block text-center mb-4">Why It Matters</span>
          <h2 
            className="uxa-h2 text-center mb-16"
            style={{ color: 'var(--ink-1)' }}
          >
            Why UX audits matter
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyMatters.map((item, i) => (
              <div key={item.stat} className="text-center p-6 rounded-xl" style={{ backgroundColor: 'var(--surface-white)', border: '1px solid var(--border-2)' }}>
                <span 
                  className="block mb-3"
                  style={{ 
                    fontSize: 'clamp(36px, 4vw, 48px)',
                    fontWeight: 700,
                    letterSpacing: '-0.04em',
                    color: 'var(--accent-text)'
                  }}
                >
                  {item.stat}
                </span>
                <h3 className="uxa-h3 mb-2" style={{ color: 'var(--ink-1)' }}>{item.title}</h3>
                <p className="uxa-dense" style={{ color: 'var(--ink-2)' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CLOSING CTA ────────────────────────────────────────────────
        *
        * On the canvas, not on dark. This was the page's only dark section;
        * the design ends on the same sand ground as everything above it, with
        * the crosshatch continuing through, so the page reads as one surface
        * rather than dropping into a different one at the last screen.
        *
        * The accent word changes token with the ground. On dark, the normal
        * accent measured 2.71:1 and failed, so a soft tint stood in. On the
        * canvas the normal accent measures 4.76:1 and passes at any size, so
        * it is used directly. The display accent is 3.18:1 - fine for large
        * text, but it is pinned to the hero lockup by the accent lint and
        * this is not the hero lockup.
        *
        * Both controls drop --on-dark for the same reason.
        */}
      <section className="uxa-pt-xl uxa-pb-xl px-6 hero uxa-cs-close">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="uxa-h2 mb-6" style={{ color: 'var(--ink-1)' }}>
            Your UX wins are <span style={{ color: 'var(--accent-text)', fontStyle: 'italic', fontWeight: 700 }}>waiting</span>.
          </h2>
          <p
            className="mb-10 max-w-xl mx-auto"
            style={{ fontSize: '16px', lineHeight: 1.7, color: 'var(--ink-2)' }}
          >
            Run your free monthly audit and see what you&rsquo;re missing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {user ? (
              <Link to="/audit/new" className="uxa-submit uxa-submit--auto">
                Start Free Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            ) : (
              <button onClick={login} className="uxa-submit uxa-submit--auto">
                Get Started Free
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            )}
            <Link to="/ux-auditor/#pricing" className="uxa-cs-cta-quiet">
              View Pricing
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
          {/* Both halves are already true on the pricing page: the free tier
              takes no card, and there is one free audit every month. */}
          <p className="uxa-cs-close-micro">No credit card &middot; One free audit every month</p>
        </div>
      </section>
    </div>
  );
}
